import { useState } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { business } from "../../config/business";
import { useEmailJS } from "../../hooks/useEmailJS";
import { sanitizeForm } from "../../utils/sanitize";
import { checkRateLimit, recordSubmission } from "../../utils/rateLimit";
import { trackEvent } from "../../lib/gtag";
import SectionWrapper from "../ui/SectionWrapper";
import Button from "../ui/Button";

const INITIAL_FORM = {
  name: "",
  phone: "",
  email: "",
  serviceType: "",
  contactMethod: "",
  message: "",
  website: "", // honeypot
};

const FORM_START_KEY = "fateh_form_start_tracked";

function trackFormStartOnce() {
  try {
    if (sessionStorage.getItem(FORM_START_KEY)) return;
    sessionStorage.setItem(FORM_START_KEY, "1");
  } catch {
    // sessionStorage unavailable — fall through and track anyway
  }
  trackEvent("form_start", { method: "contact_form" });
}

function FieldError({ msg }) {
  if (!msg) return null;
  return <p className="mt-1.5 text-red-500 text-xs font-medium">{msg}</p>;
}

function inputClass(hasError) {
  return `w-full px-4 py-3 rounded-lg border text-ink-900 bg-surface-0 text-sm placeholder:text-ink-300 focus:outline-none focus:ring-2 transition-all duration-200 ${
    hasError
      ? "border-red-400 focus:ring-red-400/30"
      : "border-surface-100 focus:border-accent-500 focus:ring-accent-500/20"
  }`;
}

export default function Contact() {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [formState, setFormState] = useState("idle"); // idle | ratelimited
  const [rateLimitMsg, setRateLimitMsg] = useState("");
  const { send, status, reset } = useEmailJS();

  const msgLength = formData.message.length;

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (formState === "ratelimited") setFormState("idle");
  }

  async function handleSubmit(e) {
    e.preventDefault();

    // 1. Honeypot check — silently fake success
    if (formData.website) {
      reset();
      // trigger the success state in the hook by setting it externally isn't possible,
      // so we just fake-navigate to the success view via formState
      setFormState("honeypot");
      return;
    }

    // 2. Rate limit check
    const rateCheck = checkRateLimit();
    if (!rateCheck.allowed) {
      setFormState("ratelimited");
      setRateLimitMsg(rateCheck.message);
      return;
    }

    // 3. Sanitize + validate
    const {
      sanitized,
      errors: validationErrors,
      isValid,
    } = sanitizeForm(formData);
    if (!isValid) {
      setErrors(validationErrors);
      // scroll to first error
      const first = Object.keys(validationErrors)[0];
      document.querySelector(`[name="${first}"]`)?.focus();
      return;
    }

    // 4. Send
    const result = await send({
      from_name: sanitized.name,
      phone: sanitized.phone,
      email: sanitized.email,
      service_type: sanitized.serviceType,
      contact_method: sanitized.contactMethod,
      message: sanitized.message || "No additional details provided.",
    });

    if (result.success) {
      recordSubmission();
      trackEvent("generate_lead", { method: "contact_form" });
    }
  }

  function handleRetry() {
    reset();
    setFormState("idle");
  }

  function handleNewRequest() {
    reset();
    setFormState("idle");
    setFormData(INITIAL_FORM);
    setErrors({});
  }

  const isSending = status === "sending";
  const isSuccess = status === "success" || formState === "honeypot";
  const isError = status === "error";

  return (
    <SectionWrapper id="contact" bg="alt">
      <div className="max-w-6xl mx-auto px-4">
        {/* Section header */}
        <div className="text-center mb-14">
          <span className="fade-in-up inline-block text-accent-500 font-semibold text-sm uppercase tracking-widest mb-3">
            Get Started
          </span>
          <h2 className="fade-in-up font-heading font-bold text-navy-900 text-4xl md:text-5xl mb-3">
            Request a Free Quote
          </h2>
          <p className="fade-in-up text-ink-600 text-lg max-w-lg mx-auto">
            Tell us what you need and we'll get back to you — usually within a
            few hours.
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-8 items-start">
          {/* ── Form column ─────────────────────────────────── */}
          <div className="fade-in-up md:col-span-3">
            {/* Rate limit banner */}
            {formState === "ratelimited" && (
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-6 text-sm">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold mb-0.5">Too many requests</p>
                  <p>{rateLimitMsg}</p>
                  <p className="mt-1">
                    Or call us directly:{" "}
                    <a
                      href={`tel:${business.phone}`}
                      onClick={() =>
                        trackEvent("phone_call_click", { method: "tel_link" })
                      }
                      className="font-semibold underline"
                    >
                      {business.phoneDisplay}
                    </a>
                  </p>
                </div>
              </div>
            )}

            {/* Success state */}
            {isSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
                <CheckCircle2
                  size={44}
                  className="text-green-500 mx-auto mb-4"
                />
                <h3 className="font-heading font-bold text-navy-800 text-2xl mb-2">
                  Request Sent!
                </h3>
                <p className="text-ink-600 mb-6">
                  Thanks — we'll get back to you within 24 hours.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <a
                    href={`tel:${business.phone}`}
                    onClick={() =>
                      trackEvent("phone_call_click", { method: "tel_link" })
                    }
                    className="inline-flex items-center justify-center gap-2 bg-navy-800 hover:bg-navy-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 text-sm"
                  >
                    <Phone size={15} />
                    Call {business.phoneDisplay}
                  </a>
                  <button
                    onClick={handleNewRequest}
                    className="inline-flex items-center justify-center gap-2 border border-surface-100 hover:border-navy-800 text-navy-800 font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 text-sm cursor-pointer"
                  >
                    <RefreshCw size={14} />
                    Submit Another Request
                  </button>
                </div>
              </div>
            )}

            {/* Error state */}
            {isError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center mb-6">
                <AlertCircle size={40} className="text-red-500 mx-auto mb-3" />
                <h3 className="font-heading font-bold text-navy-800 text-xl mb-2">
                  Something went wrong
                </h3>
                <p className="text-ink-600 mb-5 text-sm">
                  Please call us directly at{" "}
                  <a
                    href={`tel:${business.phone}`}
                    className="font-semibold text-navy-800 hover:text-accent-500 transition-colors"
                  >
                    {business.phoneDisplay}
                  </a>
                </p>
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center gap-2 text-accent-500 hover:text-accent-600 font-semibold text-sm cursor-pointer transition-colors"
                >
                  <RefreshCw size={14} />
                  Try again
                </button>
              </div>
            )}

            {/* The form */}
            {!isSuccess && (
              <form
                onSubmit={handleSubmit}
                noValidate
                className="bg-surface-0 rounded-xl border border-surface-100 p-7 md:p-9 shadow-sm"
              >
                {/* Honeypot — visually hidden, must not be filled */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-9999px",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden",
                  }}
                >
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={handleChange}
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  {/* Full Name */}
                  <div className="sm:col-span-2 md:col-span-1">
                    <label
                      htmlFor="name"
                      className="block text-sm font-semibold text-navy-800 mb-1.5"
                    >
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      placeholder="Jane Smith"
                      maxLength={100}
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={trackFormStartOnce}
                      className={inputClass(!!errors.name)}
                      aria-describedby={errors.name ? "name-error" : undefined}
                    />
                    <FieldError msg={errors.name} />
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-semibold text-navy-800 mb-1.5"
                    >
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      placeholder="519-555-0100"
                      maxLength={20}
                      value={formData.phone}
                      onChange={handleChange}
                      className={inputClass(!!errors.phone)}
                    />
                    <FieldError msg={errors.phone} />
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-semibold text-navy-800 mb-1.5"
                    >
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder="jane@example.com"
                      maxLength={254}
                      value={formData.email}
                      onChange={handleChange}
                      className={inputClass(!!errors.email)}
                    />
                    <FieldError msg={errors.email} />
                  </div>

                  {/* Service Type */}
                  <div>
                    <label
                      htmlFor="serviceType"
                      className="block text-sm font-semibold text-navy-800 mb-1.5"
                    >
                      Service Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="serviceType"
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleChange}
                      className={`${inputClass(!!errors.serviceType)} appearance-none bg-[url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236B7280' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")] bg-no-repeat bg-position-[right_14px_center] pr-10`}
                    >
                      <option value="">Select a service…</option>
                      <option value="Installation">Installation</option>
                      <option value="Repair">Repair</option>
                      <option value="Maintenance">Maintenance</option>
                      <option value="Other">Other</option>
                    </select>
                    <FieldError msg={errors.serviceType} />
                  </div>

                  {/* Preferred Contact Method */}
                  <div className="sm:col-span-2">
                    <fieldset>
                      <legend className="block text-sm font-semibold text-navy-800 mb-2.5">
                        Preferred Contact Method{" "}
                        <span className="text-red-500">*</span>
                      </legend>
                      <div className="flex flex-wrap gap-3">
                        {["Phone", "Email", "No Preference"].map((method) => (
                          <label
                            key={method}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-all duration-200 text-sm font-medium select-none ${
                              formData.contactMethod === method
                                ? "border-accent-500 bg-accent-50 text-accent-600"
                                : "border-surface-100 bg-surface-0 text-ink-600 hover:border-accent-500/40"
                            }`}
                          >
                            <input
                              type="radio"
                              name="contactMethod"
                              value={method}
                              checked={formData.contactMethod === method}
                              onChange={handleChange}
                              className="sr-only"
                            />
                            {method}
                          </label>
                        ))}
                      </div>
                      <FieldError msg={errors.contactMethod} />
                    </fieldset>
                  </div>

                  {/* Message */}
                  <div className="sm:col-span-2">
                    <div className="flex items-center justify-between mb-1.5">
                      <label
                        htmlFor="message"
                        className="block text-sm font-semibold text-navy-800"
                      >
                        Message / Details
                        <span className="text-ink-400 font-normal ml-1">
                          (optional)
                        </span>
                      </label>
                      <span
                        className={`text-xs ${msgLength > 950 ? "text-red-500" : "text-ink-400"}`}
                      >
                        {msgLength}/1000
                      </span>
                    </div>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      placeholder="Describe your equipment, issue, or any other details that would help us prepare…"
                      maxLength={1000}
                      value={formData.message}
                      onChange={handleChange}
                      className={`${inputClass(false)} resize-none`}
                    />
                  </div>
                </div>

                {/* Submit */}
                <div className="mt-7">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full sm:w-auto"
                    disabled={isSending}
                  >
                    {isSending ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Sending…
                      </>
                    ) : (
                      "Send Request"
                    )}
                  </Button>
                  <p className="text-ink-400 text-xs mt-3">
                    We typically respond within a few hours.
                  </p>
                </div>
              </form>
            )}
          </div>

          {/* ── Sidebar ─────────────────────────────────────── */}
          <div className="fade-in-up md:col-span-2">
            <div className="bg-navy-800 rounded-xl p-7 md:p-8 text-white sticky top-32">
              <h3 className="font-heading font-bold text-xl mb-7">
                Contact Info
              </h3>

              <div className="space-y-5 mb-8">
                <div className="flex items-start gap-3.5">
                  <Phone
                    size={17}
                    className="text-accent-400 shrink-0 mt-0.5"
                  />
                  <div>
                    <p className="text-ink-300 text-xs font-medium uppercase tracking-wide mb-0.5">
                      Phone
                    </p>
                    <a
                      href={`tel:${business.phone}`}
                      className="text-white font-semibold hover:text-accent-400 transition-colors duration-200"
                    >
                      {business.phoneDisplay}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3.5">
                  <Mail size={17} className="text-accent-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-ink-300 text-xs font-medium uppercase tracking-wide mb-0.5">
                      Email
                    </p>
                    <a
                      href={`mailto:${business.email}`}
                      className="text-white font-semibold hover:text-accent-400 transition-colors duration-200 break-all text-sm"
                    >
                      {business.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3.5">
                  <MapPin
                    size={17}
                    className="text-accent-400 shrink-0 mt-0.5"
                  />
                  <div>
                    <p className="text-ink-300 text-xs font-medium uppercase tracking-wide mb-0.5">
                      Address
                    </p>
                    <p className="text-white text-sm leading-relaxed">
                      {business.address.full}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3.5">
                  <Clock
                    size={17}
                    className="text-success-500 shrink-0 mt-0.5"
                  />
                  <div>
                    <p className="text-ink-300 text-xs font-medium uppercase tracking-wide mb-0.5">
                      Hours
                    </p>
                    <p className="text-success-500 font-semibold">
                      {business.hours}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-navy-700 pt-6">
                <p className="text-ink-300 text-sm mb-4">
                  Need help right away?
                </p>
                <a
                  href={`tel:${business.phone}`}
                  className="flex items-center justify-center gap-2 w-full bg-accent-500 hover:bg-accent-600 text-white font-semibold py-3.5 rounded-lg transition-all duration-200"
                >
                  <Phone size={16} />
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}
